import type { InputSize, InputVariant } from "@design-system/Input/Input.types";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { inputTextSizes, inputVariants } from "./InputPlayground.lib";
import type { InputTextSize } from "./InputPlayground.lib";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import styles from "./DatePickerPlayground.module.scss";

/**
 * @purpose The Input sandbox's props aside. Split out of `InputPlayground` so
 * the playground reads as the preview it configures. These same controls drive
 * the x-ray, so nothing here changes when the playground flips into it.
 */

export interface InputFeatureFlags {
    hasLeftIcon: boolean;
    isClearable: boolean;
    isDisabled: boolean;
    isReadOnly: boolean;
    isInvalid: boolean;
}

export const InputPlaygroundControls = ({
    variant,
    onVariantChange,
    size,
    sizeOptions,
    onSizeChange,
    textSize,
    onTextSizeChange,
    features,
    onFeaturesChange,
}: {
    variant: InputVariant;
    onVariantChange: (variant: InputVariant) => void;
    size: InputSize;
    sizeOptions: readonly InputSize[];
    onSizeChange: (size: InputSize) => void;
    textSize: InputTextSize;
    onTextSizeChange: (textSize: InputTextSize) => void;
    features: InputFeatureFlags;
    onFeaturesChange: (features: InputFeatureFlags) => void;
}) => (
    <>
        <section>
            <h2>Variant</h2>
            <PlaygroundSelect<InputVariant>
                options={inputVariants}
                value={variant}
                onChange={onVariantChange}
                searchPlaceholder="Search variants..."
            />
            <h2 className={styles.subheading}>Size</h2>
            <ToggleGroup.Root
                value={size}
                onValueChange={(value) => {
                    if (value) {
                        onSizeChange(value);
                    }
                }}
            >
                {sizeOptions.map((option) => (
                    <ToggleGroup.Item key={option} value={option}>
                        {option}
                    </ToggleGroup.Item>
                ))}
            </ToggleGroup.Root>
            {variant === "ghost" ? (
                <>
                    <h2 className={styles.subheading}>Text size</h2>
                    <ToggleGroup.Root
                        value={textSize}
                        onValueChange={(value) => {
                            if (value) {
                                onTextSizeChange(value);
                            }
                        }}
                    >
                        {inputTextSizes.map((option) => (
                            <ToggleGroup.Item key={option} value={option}>
                                {option}
                            </ToggleGroup.Item>
                        ))}
                    </ToggleGroup.Root>
                </>
            ) : null}
        </section>
        <section>
            <h2>Features</h2>
            <div className={styles.booleanGrid}>
                <BooleanControl
                    label="Left icon"
                    checked={features.hasLeftIcon}
                    onChange={(hasLeftIcon) =>
                        onFeaturesChange({ ...features, hasLeftIcon })
                    }
                />
                <BooleanControl
                    label="Clearable"
                    checked={features.isClearable}
                    onChange={(isClearable) =>
                        onFeaturesChange({ ...features, isClearable })
                    }
                />
                <BooleanControl
                    label="Disabled"
                    checked={features.isDisabled}
                    onChange={(isDisabled) =>
                        onFeaturesChange({ ...features, isDisabled })
                    }
                />
                <BooleanControl
                    label="Read-only"
                    checked={features.isReadOnly}
                    onChange={(isReadOnly) =>
                        onFeaturesChange({ ...features, isReadOnly })
                    }
                />
                {variant === "primary" ? (
                    <BooleanControl
                        label="Invalid"
                        checked={features.isInvalid}
                        onChange={(isInvalid) =>
                            onFeaturesChange({ ...features, isInvalid })
                        }
                    />
                ) : null}
            </div>
        </section>
    </>
);
